import React, { useState, useEffect, useCallback } from 'react';
import {
    Descriptions,
    Spin,
    Alert,
    Table,
    Button,
    Divider,
    Typography,
    Tooltip,
    Input,
    Select,
    DatePicker,
    Row,
    Col,
    message,
} from 'antd';
import {
    DownloadOutlined, FolderViewOutlined,
    ReloadOutlined,
    FilePdfOutlined,
    WarningOutlined,
    FileExcelOutlined, FileZipOutlined,
} from '@ant-design/icons';
import { getDataFilePO } from '../../services/plaOperativo/get_poaDocumentosMedios';
import { descargaSoportes } from '../../services/plaOperativo/headerPlanOperativoServices';

import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';

const { Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const FileManagerPP = ({ codigo_tarea, select_proyect }) => {
    const [loading, setLoading] = useState(false);
    const [dataFile, setDataFile] = useState([]);           // Todos los registros
    const [filteredData, setFilteredData] = useState([]);   // Registros tras filtros
    const [errorMsg, setErrorMsg] = useState('');
    const [soporteFilter, setSoporteFilter] = useState(''); // texto para filtrar nameResource
    const [userFilter, setUserFilter] = useState('');       // usuario seleccionado
    const [dateRange, setDateRange] = useState([]);         // [moment, moment] para fechaDocumento

    const codIndicador = codigo_tarea.codIndicador;




    // Columnas para la tabla de Ant Design
    const columns = [
        {
            title: 'Acciones',
            key: 'acciones',
            render: (_, record) => (
                <Button
                    type="link"
                    href={record.fullPath}
                    target="_blank"
                    rel="noopener noreferrer"
                    icon={<FolderViewOutlined />}
                >
                    Ver soporte
                </Button>
            ),
            width: 120,
        },
        {
            title: 'Soporte',
            dataIndex: 'nameResource',
            key: 'nameResource',
            ellipsis: true,
            fixed: 'left',
            align: 'left',
            render: (text) => (
                <span>
                    <FilePdfOutlined style={{ color: 'red', marginRight: 8 }} />
                    {text}
                </span>
            ),
        },
        {
            title: 'Fecha del soporte',
            dataIndex: 'fechaSoporte',
            key: 'fechaSoporte',
            align: 'center',
            render: (fechaSoporte) => {
                if (!fechaSoporte) return '-';
                return new Date(fechaSoporte).toLocaleDateString('es-ES');
            },
            sorter: (a, b) => new Date(a.fechaSoporte) - new Date(b.fechaSoporte),
        },
        {
            title: 'Fecha de carga',
            dataIndex: 'fechaDocumento',
            key: 'fechaDocumento',
            align: 'center',
            render: (fechaDocumento) => {
                if (!fechaDocumento) return '-';
                return new Date(fechaDocumento).toLocaleDateString('es-ES');
            },
            sorter: (a, b) => new Date(a.fechaDocumento) - new Date(b.fechaDocumento),
        },
        {
            title: 'Avance Reportado',
            key: 'avanceReportado',
            align: 'center',
            render: (_, record) => {
                try {
                    const obj = JSON.parse(record.detailJSON);
                    return obj.avanceReportado != null ? obj.avanceReportado : '-';
                } catch {
                    return '-';
                }
            },
            sorter: (a, b) => {
                try {
                    const ai = JSON.parse(a.detailJSON).avanceReportado || 0;
                    const bi = JSON.parse(b.detailJSON).avanceReportado || 0;
                    return ai - bi;
                } catch {
                    return 0;
                }
            },
        },
        {
            title: 'Observaciones',
            key: 'observacionesJSON',
            align: 'left',
            render: (_, record) => {
                let texto = '-';
                try {
                    const obj = JSON.parse(record.detailJSON);
                    texto = obj.observaciones || '-';
                } catch {
                    texto = '-';
                }

                return (
                    <Tooltip
                        title={
                            <div
                                style={{
                                    maxWidth: 400,
                                    maxHeight: 200,
                                    overflowY: 'auto',
                                    whiteSpace: 'pre-wrap',
                                }}
                            >
                                {texto}
                            </div>
                        }
                    >
                        <span
                            style={{
                                display: 'inline-block',
                                maxWidth: 200,
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                            }}
                        >
                            {texto}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Medio Verificación',
            dataIndex: 'medio_verificacion',
            key: 'medio_verificacion',
        },
        {
            title: 'Tipo Actividad',
            dataIndex: 'tipoActividad',
            key: 'tipoActividad',
        },
        {
            title: 'Usuario',
            dataIndex: 'usuario_documento',
            key: 'usuario_documento',
            render: (usr) => usr || '-',
        },
    ];

    // 1) Cargar datos desde el servicio
    // 1) Extraemos la función de “fetchFilesPO” para poder llamarla desde useEffect y desde el botón
    const fetchFilesPO = useCallback(async () => {
        setLoading(true);
        setErrorMsg('');
        setDataFile([]);
        setFilteredData([]);

        try {
            const responseObject = await getDataFilePO(codIndicador);

            if (responseObject.status !== 200) {
                // Si llega un status distinto, forzamos el catch
                throw new Error(
                    `${responseObject.status}: El Indicador no cuenta con soportes cargados en el POA.`
                );
            }

            const documentos = responseObject.data || [];

            if (documentos.length > 0) {
                setDataFile(documentos);
                setFilteredData(documentos);
            } else {
                setErrorMsg('El indicador POA no tiene soportes cargados.');
            }
        } catch (err) {
            setErrorMsg(err.message || 'Error inesperado al obtener documentos.');
        } finally {
            setLoading(false);
        }
    }, [codIndicador]);

    // 2) Llamamos a fetchFilesPO() cuando cambian select_proyect o codIndicador
    useEffect(() => {
        fetchFilesPO();
    }, [select_proyect, codIndicador, fetchFilesPO]);


    const handleDownload = () => {
        descargaSoportes(select_proyect, codIndicador)
            .catch(() => {
                message.error('No se pudieron descargar los soportes.');
            });
    };


    // 2) Cada vez que cambie dataFile, soporteFilter, userFilter o dateRange, recalcular filteredData
    useEffect(() => {
        let temp = [...dataFile];

        // Filtrar por texto en nameResource (Soporte)
        if (soporteFilter) {
            const lower = soporteFilter.toLowerCase();
            temp = temp.filter((item) =>
                item.nameResource.toLowerCase().includes(lower)
            );
        }

        // Filtrar por usuario
        if (userFilter) {
            temp = temp.filter((item) => item.usuario_documento === userFilter);
        }

        // Filtrar por rango de fecha en fechaDocumento
        if (dateRange && dateRange.length === 2) {
            const [start, end] = dateRange;
            temp = temp.filter((item) => {
                const fd = item.fechaDocumento ? new Date(item.fechaDocumento) : null;
                if (!fd) return false;
                return (
                    fd >= start.startOf('day').toDate() && fd <= end.endOf('day').toDate()
                );
            });
        }

        setFilteredData(temp);
    }, [dataFile, soporteFilter, userFilter, dateRange]);

    // 3) Obtener lista única de usuarios para el Select
    const usuariosUnicos = Array.from(
        new Set(dataFile.map((item) => item.usuario_documento).filter(Boolean))
    );


    // 4) Función para exportar a Excel
    const exportToExcel = async () => {
        // 4.1 crear un nuevo workbook
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Medios de Verificación');

        // 4.2 definir cabecera (mismos títulos que en columns, excepto “Acciones”)
        worksheet.columns = [
            { header: 'Soporte', key: 'nameResource', width: 30 },
            { header: 'Fecha del soporte', key: 'fechaSoporte', width: 15 },
            { header: 'Fecha de carga', key: 'fechaDocumento', width: 15 },
            { header: 'Avance Reportado', key: 'avanceReportado', width: 15 },
            { header: 'Observaciones', key: 'observaciones', width: 50 },
            { header: 'Medio Verificación', key: 'medio_verificacion', width: 30 },
            { header: 'Tipo Actividad', key: 'tipoActividad', width: 30 },
            { header: 'Usuario', key: 'usuario_documento', width: 20 },
        ];

        // ——————————————
        // PINTAR LAS CABECERAS
        const headerRow = worksheet.getRow(1);
        headerRow.eachCell((cell) => {
            // fondo sólido
            cell.fill = {
                type: 'pattern',
                pattern: 'solid',
                fgColor: { argb: 'FF0869C6' },
            };
            // opcional: texto blanco y negrita
            cell.font = {
                color: { argb: 'FFFFFFFF' },
                bold: true,
            };
            // opcional: centrar texto horizontal y vertical
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
        });
        // ——————————————

        // 4.3 agregar filas con filteredData
        filteredData.forEach((item) => {
            let avance = '';
            let obs = '';
            try {
                const obj = JSON.parse(item.detailJSON);
                avance = obj.avanceReportado != null ? obj.avanceReportado : '';
                obs = obj.observaciones || '';
            } catch {
                avance = '';
                obs = '';
            }

            worksheet.addRow({
                nameResource: item.nameResource,
                fechaSoporte: item.fechaSoporte
                    ? new Date(item.fechaSoporte).toLocaleDateString('es-ES')
                    : '',
                fechaDocumento: item.fechaDocumento
                    ? new Date(item.fechaDocumento).toLocaleDateString('es-ES')
                    : '',
                avanceReportado: avance,
                observaciones: obs,
                medio_verificacion: item.medio_verificacion,
                tipoActividad: item.tipoActividad,
                usuario_documento: item.usuario_documento,
            });
        });

        // 4.4 generar buffer y descargar
        const buf = await workbook.xlsx.writeBuffer();
        saveAs(
            new Blob([buf], { type: 'application/octet-stream' }),
            'Medios_de_Verificacion.xlsx'
        );
    };

    return (
        <div className="container-fluid py-0 my-2" style={{ marginBottom: '10px' }}>
            <Descriptions bordered column={1} style={{ padding: '0 0 0 10px' }}>
                <Descriptions.Item label="Actividad">
                    {codigo_tarea.codActividad} - {codigo_tarea.actividad}
                </Descriptions.Item>
                <Descriptions.Item label="Tarea">
                    {codigo_tarea.tarea}
                </Descriptions.Item>
                <Descriptions.Item label="Indicador">
                    {codigo_tarea.indicador}
                </Descriptions.Item>
                <Descriptions.Item label="Medio de verificación">
                    {codigo_tarea.medioVerificacion}
                </Descriptions.Item>
            </Descriptions>

            {loading ? (
                <div style={{ textAlign: 'center', padding: '40px 0' }}>
                    <Spin size="large" />
                </div>
            ) : errorMsg ? (
                <Alert
                    message={errorMsg}
                    type="warning"
                    showIcon
                    icon={<WarningOutlined />}
                    action={
                        <Button
                            size="small"
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                // Simplemente volvemos a llamar a fetchFilesPO()
                                fetchFilesPO();
                            }}
                        >
                            Reintentar
                        </Button>
                    }
                    style={{ margin: '16px' }}
                />
            ) : (
                <>
                    {/* ——— FILTROS SOBRE LA TABLA ——— */}
                    <Row gutter={16} style={{ margin: '16px 0' }}>
                        {/* 1. Input para filtrar por nombre de soporte */}
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Input
                                placeholder="Buscar por nombre de soporte"
                                value={soporteFilter}
                                onChange={(e) => setSoporteFilter(e.target.value)}
                                allowClear
                            />
                        </Col>

                        {/* 2. Select para filtrar por usuario */}
                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Select
                                placeholder="Filtrar por usuario"
                                value={userFilter || undefined}
                                onChange={(val) => setUserFilter(val)}
                                allowClear
                                style={{ width: '100%' }}
                            >
                                {usuariosUnicos.map((usr) => (
                                    <Option key={usr} value={usr}>
                                        {usr}
                                    </Option>
                                ))}
                            </Select>
                        </Col>

                        {/* 3. RangePicker para filtrar por fecha de carga */}
                        <Col xs={24} sm={24} md={8} lg={6}>
                            <RangePicker
                                style={{ width: '100%' }}
                                onChange={(dates) => {
                                    setDateRange(dates || []);
                                }}
                                allowClear
                            />
                        </Col>

                        <Col xs={24} sm={12} md={8} lg={6}>
                            <Button
                                type="primary"
                                icon={<FileExcelOutlined />}
                                onClick={exportToExcel}
                            >
                                Exportar
                            </Button>
                            <Button
                                style={{ marginLeft: 8 }}
                                type="primary"
                                icon={<FileZipOutlined />}
                                onClick={handleDownload}
                            >
                                Descargar
                            </Button>
                        </Col>

                    </Row>

                    {/* ——— TABLA CON DATOS FILTRADOS ——— */}
                    <Table
                        columns={columns}
                        dataSource={filteredData.map((item) => ({
                            key: item.documento_oid,
                            ...item,
                        }))}
                        size="small"
                        pagination={{ pageSize: 20, showSizeChanger: false }}
                        tableLayout="fixed"
                        scroll={{ x: 'max-content' }}
                        style={{ margin: '16px 0' }}
                    />
                </>
            )}
        </div>
    );
};

export default FileManagerPP;
